'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SlotDurationMs, msToSlotsNum } from '@velocity-exchange/sdk';
import { useRiskCeilingSlotClock } from 'src/hooks/useSlotClock';
import { slotQueryKeys } from 'src/hooks/query/slot/slotQueryKeys';
import { SlotState } from 'src/hooks/query/slot/useSlotQuery';
import { slotSubject } from 'src/utils/slotMetrics';
import { useSetupUserAccountStateAndListeners } from 'src/hooks/user/useFetchAndSubscribeSubaccounts/useSetupUserAccountStateAndListeners';
import {
	SubscriptionTabRecoveryTiming,
	useSubscriptionTabRecovery,
} from 'src/hooks/utils/useSubscriptionTabRecovery';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { AccountData } from 'src/stores/useVelocityAccountsStore';
import { getAccountTradingDataLastFreshMs } from './accountTradingDataFreshness';

export type AccountFreshnessVerdict =
	| 'fresh'
	/** Account data is provably behind the chain — refetch it. */
	| 'stale'
	/** The `User` has no live subscription — needs a full resubscribe. */
	| 'unsubscribed'
	/** Not enough trustworthy information to judge. Do nothing. */
	| 'unknown';

export interface AccountFreshnessThresholds {
	/**
	 * Wall-clock slot lag beyond which an account is a staleness candidate.
	 * Converted to slots at the live slot length, flooring: a marginally
	 * tighter ceiling is the safe direction.
	 */
	maxSlotLagMs: number;
	/**
	 * Wall-clock floor. Slot lag alone is not enough: under a websocket account
	 * subscriber a legitimately quiet account emits nothing, so its slot sits
	 * still while the chain moves on. We only act once the UI has also gone this
	 * long without fresh data.
	 */
	minStaleWallClockMs: number;
	/**
	 * If our own slot feed has not advanced within this window we cannot judge
	 * anything — every account would look stale. Do nothing instead.
	 */
	maxCurrentSlotAgeMs: number;
}

/**
 * Deliberately generous. The event-driven reconcile in the market event
 * listener is the fast path; this watchdog is the slow safety net, and the app
 * already has known RPC-load problems.
 */
export const ACCOUNT_FRESHNESS_THRESHOLDS: AccountFreshnessThresholds = {
	maxSlotLagMs: 10 * 60_000,
	minStaleWallClockMs: 5 * 60_000,
	maxCurrentSlotAgeMs: 60_000,
};

export const ACCOUNT_FRESHNESS_RECOVERY_TIMING: SubscriptionTabRecoveryTiming =
	{
		fallbackCooldownMs: 5 * 60_000,
		recoveryCheckIntervalMs: 60_000,
		// Nothing is torn down while hidden; this only sets how long the tab must be
		// hidden before a refocus triggers a freshness sweep.
		tabInactiveDisconnectDelayMs: 60_000,
		maxRecoveryAttempts: 3,
	};

/** How often the local (RPC-free) health evaluation runs. */
const HEALTH_CHECK_INTERVAL_MS = 30_000;

export interface AccountFreshnessInput {
	isSubscribed: boolean;
	/** Slot of the SDK's locally cached user account, if any. */
	accountSlot: number | undefined;
	currentSlot: number;
	/** Whether the slot subscription itself is alive. */
	currentSlotIsSubscribed: boolean;
	/** Wall-clock ms since `currentSlot` last advanced. */
	currentSlotAgeMs: number;
	/** Wall-clock ms since this account's data was last pushed to the UI. */
	msSinceLastFreshData: number;
	/** Live slot length, for converting the wall-clock lag ceiling to slots. */
	slotDurationMs: SlotDurationMs;
	thresholds?: AccountFreshnessThresholds;
}

/**
 * Slot-lag with a wall-clock floor, guarded on the health of the slot feed.
 * Pure so the decision can be tested without a React tree.
 */
export function evaluateAccountFreshness({
	isSubscribed,
	accountSlot,
	currentSlot,
	currentSlotIsSubscribed,
	currentSlotAgeMs,
	msSinceLastFreshData,
	slotDurationMs,
	thresholds = ACCOUNT_FRESHNESS_THRESHOLDS,
}: AccountFreshnessInput): AccountFreshnessVerdict {
	if (!isSubscribed) return 'unsubscribed';

	// Never conclude "stale" from an untrustworthy clock source.
	if (!currentSlotIsSubscribed) return 'unknown';
	if (currentSlot <= 0) return 'unknown';
	if (currentSlotAgeMs > thresholds.maxCurrentSlotAgeMs) return 'unknown';

	if (msSinceLastFreshData < thresholds.minStaleWallClockMs) return 'fresh';

	// Subscribed but the SDK holds no account at all — nothing to compare, and
	// the wall-clock floor has already passed.
	if (accountSlot === undefined) return 'stale';

	const maxSlotLagSlots = msToSlotsNum(thresholds.maxSlotLagMs, slotDurationMs);

	return currentSlot - accountSlot > maxSlotLagSlots ? 'stale' : 'fresh';
}

/**
 * Watches every subaccount's on-chain freshness and recovers stale ones. A
 * `User` that dies mid-session is otherwise unwatched: the trading data caches
 * are push-only, their only writer bails when unsubscribed, and the
 * orchestrator re-evaluates only when the set of subaccount keys changes.
 *
 * Recovery runs only while `isHealthy` is false, and each sweep re-evaluates
 * before touching anything.
 *
 * An account's own slot is the only evidence available, and a quiet account is
 * indistinguishable from a dead socket by that measure: both sit still while
 * the chain moves. So a genuinely idle subaccount does cross `maxSlotLagMs`
 * and draw one `fetchAccounts()`, which advances its slot and resets the clock
 * — roughly one refetch per idle subaccount per `maxSlotLagMs` of chain
 * time. That false positive is the deliberate price of catching a socket that
 * died silently, which is the failure this exists for.
 */
export function useUserAccountFreshnessWatchdog(
	accounts: AccountData[],
	enabled: boolean
): void {
	const queryClient = useQueryClient();
	const accountsRef = useSyncedRef(accounts);

	const { setupUserAccountStateAndListeners } =
		useSetupUserAccountStateAndListeners();

	// The lag ceiling is wall-clock, so the sweep needs the live slot length.
	// Kept in a ref because it is only ever read inside the 30s interval.
	const slotDurationMsRef = useSyncedRef(
		useRiskCeilingSlotClock().slotDurationMs
	);

	// Slot values come off the rxjs feed rather than `useSlotQuery`, and the
	// sweep only reads them inside its own 30s interval.
	const currentSlotRef = useRef(0);
	const currentSlotChangedAtRef = useRef(Date.now());
	useEffect(() => {
		const sub = slotSubject.subscribe((slot) => {
			if (slot === currentSlotRef.current) return;
			currentSlotRef.current = slot;
			currentSlotChangedAtRef.current = Date.now();
		});
		return () => sub.unsubscribe();
	}, []);

	// Accounts that have never pushed data are measured from mount, so a fresh
	// page load gets the full wall-clock grace period.
	const mountedAtRef = useRef(Date.now());
	const lastFallbackTimestamp = useRef(0);
	const [isHealthy, setIsHealthy] = useState(true);

	const evaluateAll = useCallback((): {
		account: AccountData;
		verdict: AccountFreshnessVerdict;
	}[] => {
		const now = Date.now();
		const currentSlotAgeMs = now - currentSlotChangedAtRef.current;
		// Read, not subscribed to — see the note on `currentSlotRef`.
		const currentSlotIsSubscribed =
			queryClient.getQueryData<SlotState | null>(slotQueryKeys.slot())
				?.isSubscribed ?? false;

		return accountsRef.current.map((account) => {
			const user = account.client;
			if (!user) return { account, verdict: 'unknown' as const };

			let accountSlot: number | undefined;
			try {
				accountSlot = user.getUserAccountAndSlot()?.slot;
			} catch {
				accountSlot = undefined;
			}

			const lastFreshMs =
				getAccountTradingDataLastFreshMs(account.userKey) ??
				mountedAtRef.current;

			return {
				account,
				verdict: evaluateAccountFreshness({
					isSubscribed: user.isSubscribed,
					accountSlot,
					currentSlot: currentSlotRef.current,
					currentSlotIsSubscribed,
					currentSlotAgeMs,
					msSinceLastFreshData: now - lastFreshMs,
					slotDurationMs: slotDurationMsRef.current,
				}),
			};
		});
	}, [accountsRef, queryClient, slotDurationMsRef]);

	// Local, RPC-free health evaluation. Drives the shared recovery module's
	// `isHealthy` input (and therefore its attempt-counter reset).
	useEffect(() => {
		if (!enabled) {
			setIsHealthy(true);
			return;
		}

		const check = () => {
			const unhealthy = evaluateAll().some(
				({ verdict }) => verdict === 'stale' || verdict === 'unsubscribed'
			);
			setIsHealthy(!unhealthy);
		};

		check();
		const interval = setInterval(check, HEALTH_CHECK_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [enabled, evaluateAll]);

	const performRecovery = useCallback(() => {
		const results = evaluateAll();
		const resubscribes: Promise<boolean>[] = [];
		let issuedWork = false;

		for (const { account, verdict } of results) {
			const user = account.client;
			if (!user) continue;

			if (verdict === 'unsubscribed') {
				// A refetch would land in a `User` nothing is listening to; the socket
				// has to be rebuilt before anything else is worth doing. Each account
				// is recovered on its own so one that stays dead cannot block the rest.
				resubscribes.push(
					user.subscribe().catch((e) => {
						console.error(
							`[accountFreshness] resubscribe failed for ${account.userKey}`,
							e
						);
						return false;
					})
				);
				continue;
			}
			if (verdict !== 'stale') continue;

			issuedWork = true;
			user.fetchAccounts().catch((e) => {
				console.error(
					`[accountFreshness] fetchAccounts failed for ${account.userKey}`,
					e
				);
			});
		}

		if (resubscribes.length > 0) {
			issuedWork = true;
			void Promise.all(resubscribes).then((outcomes) => {
				// Rewiring the store listeners only makes sense once something is
				// actually back on a live socket.
				if (!outcomes.some(Boolean)) return;
				setupUserAccountStateAndListeners();
			});
		}

		// Only arm the cooldown when we actually spent RPC. A sweep that found
		// nothing to do should not delay the next real attempt.
		if (issuedWork) {
			lastFallbackTimestamp.current = Date.now();
		}
	}, [evaluateAll, setupUserAccountStateAndListeners]);

	const noop = useCallback(() => {}, []);

	useSubscriptionTabRecovery({
		isHealthy,
		recoveryEnabled: enabled,
		lastFallbackTimestamp,
		performRecovery,
		// Nothing to tear down while the tab is hidden — the sweep is stateless.
		onTabInactiveCleanup: noop,
		timing: ACCOUNT_FRESHNESS_RECOVERY_TIMING,
	});
}
